/**
 * Module: useWithdrawFlow
 * Role: Withdraw controller for the WalletScreen — explicit NEW vs REPLAY_UNKNOWN
 *       branches, fresh balance before a new send, stored key+amount pair, polling.
 * Uses: @tanstack/react-query (useQuery/useQueryClient/useMutation), apiFetch, formatKopecks.
 * Used by: screens/WalletScreen.tsx.
 * Glossary: ai/glossary/buyer.md
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import { formatKopecks } from '../../lib/format';

export interface BalanceView {
  available_kopecks: number;
  pending_kopecks: number;
  next_accrual_date: string | null;
  accrued_total_kopecks: number;
  withdrawn_total_kopecks: number;
  currency: 'RUB';
  calculated_at: string;
}
export interface ProfileView {
  sbp_complete: boolean;
  sbp_phone_masked?: string | null;
  sbp_bank_name?: string | null;
  fio_display?: string | null;
  [key: string]: unknown;
}
interface WithdrawInitResponse { withdrawal_id: string; status: string; }
interface WithdrawStatusResponse { withdrawal_id: string; status: string; reason?: string | null; }
interface SavedAttempt { key: string; amount_kopecks: number; }

const IDEM_KEY = 'awardy:withdraw:idem-key';
const PENDING_ID_KEY = 'awardy:withdraw:pending-id';
const ATTEMPT_KEY = 'awardy:withdraw:attempt';
const TERMINAL_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
// SYMMETRIC LIMIT (ADR-022) — sync with src/shared/limits.py:92 MIN_WITHDRAWAL_KOPECKS
export const MIN_WITHDRAWAL_KOPECKS = 50000;
const LEGACY_MESSAGE = 'Не удалось восстановить прошлый вывод. Напиши в @awardy_support';
const BALANCE_QUERY_KEY = ['earnings', 'balance'] as const;
const PROFILE_QUERY_KEY = ['buyer', 'profile'] as const;

function normalizeWithdrawStatus(status?: string | null): string | undefined {
  return status?.toUpperCase();
}

function mapWithdrawError(err: unknown): string | null {
  if (!(err instanceof Error)) return null;
  const e = err as Error & { status?: number; code?: string; fieldErrors?: Array<{ field: string; code: string }> };
  const msg = e.message;
  if (e.fieldErrors?.some((fe) => fe.field === 'amount_kopecks')) {
    return `Минимальная сумма вывода — ${formatKopecks(MIN_WITHDRAWAL_KOPECKS)}`;
  }
  if (e.code === 'TRUST_TOO_LOW') return 'Вывод недоступен из-за низкого рейтинга доверия. Напиши в @awardy_support';
  if (e.code === 'VELOCITY_BLOCKED') return 'Слишком много выводов подряд. Попробуй позже';
  if (msg.includes('API 402')) return 'Недостаточно средств';
  if (msg.includes('API 409')) return 'Попробуй ещё раз';
  if (msg.includes('API 429')) return 'Вывод уже обрабатывается. Подожди минуту';
  if (msg.includes('API 404')) return 'Вывод временно недоступен';
  if (e.status === 403) return 'Вывод временно ограничен, напиши в @awardy_support';
  if (e.status !== undefined && e.status >= 500) return 'Сервис выплат недоступен, попробуй позже';
  return 'Произошла ошибка';
}

function readPair(): SavedAttempt | null {
  const raw = sessionStorage.getItem(ATTEMPT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SavedAttempt>;
    if (
      typeof parsed?.key === 'string'
      && parsed.key.length > 0
      && Number.isInteger(parsed?.amount_kopecks)
      && (parsed.amount_kopecks as number) > 0
    ) {
      return { key: parsed.key, amount_kopecks: parsed.amount_kopecks as number };
    }
  } catch {
    return null;
  }
  return null;
}

function savePair(pair: SavedAttempt): void {
  // Pair is persisted before POST so a lost response never recomputes the amount.
  sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(pair));
  sessionStorage.setItem(IDEM_KEY, pair.key);
}

function clearWithdrawSession(): void {
  sessionStorage.removeItem(ATTEMPT_KEY);
  sessionStorage.removeItem(IDEM_KEY);
  sessionStorage.removeItem(PENDING_ID_KEY);
}

export interface WithdrawFlow {
  withdrawalId: string | null;
  isPolling: boolean;
  pollTimedOut: boolean;
  isSending: boolean;
  success: boolean;
  failReason: string | null;
  error: string | null;
  legacyBlocked: boolean;
  handleWithdraw: () => void;
  handleRetry: () => void;
}

export function useWithdrawFlow({ navigate }: { navigate: (path: string) => void }): WithdrawFlow {
  const queryClient = useQueryClient();
  const [withdrawalId, setWithdrawalId] = useState<string | null>(() =>
    sessionStorage.getItem(PENDING_ID_KEY)
  );
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [failReason, setFailReason] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() =>
    !readPair() && sessionStorage.getItem(IDEM_KEY) ? LEGACY_MESSAGE : null
  );
  const [legacyBlocked, setLegacyBlocked] = useState(() => !readPair() && sessionStorage.getItem(IDEM_KEY) !== null);
  const busyRef = useRef(false);
  const pollStartRef = useRef<number | null>(null);

  const { data: pollData } = useQuery<WithdrawStatusResponse>({
    queryKey: ['withdrawal', withdrawalId],
    queryFn: () => apiFetch<WithdrawStatusResponse>(`/api/v2/buyer/earnings/withdraw/${withdrawalId}`),
    enabled: !!withdrawalId && !pollTimedOut,
    refetchInterval: (query) => {
      const pollStatus = normalizeWithdrawStatus(query.state.data?.status);
      if (pollStatus && TERMINAL_STATUSES.has(pollStatus)) return false;
      if (pollStartRef.current !== null && Date.now() - pollStartRef.current >= POLL_TIMEOUT_MS) return false;
      return 3000;
    },
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!withdrawalId || pollTimedOut) return;
    if (pollStartRef.current === null) pollStartRef.current = Date.now();
    const timer = setTimeout(() => setPollTimedOut(true), POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [withdrawalId, pollTimedOut]);

  useEffect(() => {
    if (!pollData) return;
    const pollStatus = normalizeWithdrawStatus(pollData.status);
    if (pollStatus === 'COMPLETED') {
      void queryClient.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
      clearWithdrawSession();
      setWithdrawalId(null);
      setError(null);
      setFailReason(null);
      setLegacyBlocked(false);
      setSuccess(true);
    } else if (pollStatus === 'FAILED' || pollStatus === 'CANCELLED') {
      setFailReason(pollData.reason ?? 'Вывод не выполнен');
      setError(null);
      clearWithdrawSession();
      setWithdrawalId(null);
      setLegacyBlocked(false);
      void queryClient.invalidateQueries({ queryKey: BALANCE_QUERY_KEY });
    }
  }, [pollData, queryClient]);

  const withdrawMutation = useMutation<WithdrawInitResponse, Error, SavedAttempt>({
    mutationFn: (pair) => apiFetch<WithdrawInitResponse>('/api/v2/buyer/earnings/withdraw', {
      method: 'POST',
      headers: { 'Idempotency-Key': pair.key },
      body: JSON.stringify({ amount_kopecks: pair.amount_kopecks }),
    }),
  });

  const postPair = useCallback(async (pair: SavedAttempt): Promise<void> => {
    try {
      const res = await withdrawMutation.mutateAsync(pair);
      setWithdrawalId(res.withdrawal_id);
      sessionStorage.setItem(PENDING_ID_KEY, res.withdrawal_id);
      pollStartRef.current = Date.now();
      setSuccess(false);
      setError(null);
      setFailReason(null);
    } catch (err) {
      const e = err as Error & { status?: number };
      const message = e.message ?? '';
      if (message.includes('API 422') && message.includes('SBP_PROFILE_INCOMPLETE')) {
        clearWithdrawSession();
        setLegacyBlocked(false);
        navigate('/requisites');
        return;
      }
      // 402/422 are confirmed rejections — no withdrawal exists, so the next
      // attempt is NEW and must recompute from a fresh balance.
      if (e.status === 402 || e.status === 422) {
        clearWithdrawSession();
        setLegacyBlocked(false);
      }
      // 409, 429, 5xx and transport errors keep key+amount for REPLAY_UNKNOWN.
      setError(mapWithdrawError(err));
    }
  }, [withdrawMutation, navigate]);

  const handleWithdraw = useCallback(() => {
    if (busyRef.current || withdrawalId) return;
    busyRef.current = true;
    setIsSending(true);
    setError(null);
    setFailReason(null);

    void (async () => {
      try {
        const pair = readPair();
        if (pair) {
          // REPLAY_UNKNOWN: the previous POST may already have created a
          // withdrawal. Never recalculate amount, rotate key or apply guards.
          await postPair(pair);
          return;
        }
        if (sessionStorage.getItem(IDEM_KEY)) {
          setLegacyBlocked(true);
          setError(LEGACY_MESSAGE);
          return;
        }

        let fresh: { balance: BalanceView; profile: ProfileView };
        try {
          const [balance, profile] = await Promise.all([
            queryClient.fetchQuery<BalanceView>({
              queryKey: BALANCE_QUERY_KEY,
              queryFn: () => apiFetch<BalanceView>('/api/v2/buyer/earnings/balance'),
              retry: false,
              staleTime: 0,
            }),
            queryClient.fetchQuery<ProfileView>({
              queryKey: PROFILE_QUERY_KEY,
              queryFn: () => apiFetch<ProfileView>('/api/v2/buyer/profile'),
              retry: false,
              staleTime: 0,
            }),
          ]);
          fresh = { balance, profile };
        } catch {
          setError('Не удалось обновить баланс. Попробуй ещё раз');
          return;
        }

        const pendingId = sessionStorage.getItem(PENDING_ID_KEY);
        if (pendingId) {
          setWithdrawalId(pendingId);
          return;
        }
        if (!fresh.profile.sbp_complete) {
          navigate('/requisites');
          return;
        }
        const amount = fresh.balance.available_kopecks;
        if (amount < MIN_WITHDRAWAL_KOPECKS) {
          setError(amount > 0
            ? `Вывод от ${formatKopecks(MIN_WITHDRAWAL_KOPECKS)} — накопи ещё ${formatKopecks(MIN_WITHDRAWAL_KOPECKS - amount)}`
            : 'Недостаточно средств');
          return;
        }

        const newPair = { key: crypto.randomUUID(), amount_kopecks: amount };
        savePair(newPair);
        await postPair(newPair);
      } finally {
        busyRef.current = false;
        setIsSending(false);
      }
    })();
  }, [withdrawalId, postPair, queryClient, navigate]);

  return {
    withdrawalId,
    isPolling: !!withdrawalId && !pollTimedOut,
    pollTimedOut,
    isSending,
    success,
    failReason,
    error,
    legacyBlocked,
    handleWithdraw,
    handleRetry: handleWithdraw,
  };
}
